import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShoppingBag, X } from "lucide-react";
import { shopApi, formatApiError } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const COUNTRY_LABEL = { DE: "Deutschland", AT: "Österreich", CH: "Schweiz" };

function absoluteImageUrl(url) {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    return `${BACKEND_URL}${url}`;
}

export default function ShopSection() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [rates, setRates] = useState({ DE: 2.9, AT: 6.5, CH: 6.5 });
    const [openProduct, setOpenProduct] = useState(null);

    useEffect(() => {
        shopApi
            .products()
            .then((d) => {
                setProducts(d.products || []);
                if (d.shipping_rates_eur) setRates(d.shipping_rates_eur);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    if (loading) return null;
    if (products.length === 0) return null;

    return (
        <section
            className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24"
            data-testid="shop-section"
        >
            <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
                <div>
                    <div className="label mb-3 inline-flex items-center gap-2">
                        <ShoppingBag size={14} /> Shop für die Tauben
                    </div>
                    <h2 className="font-display text-3xl sm:text-4xl tracking-tight max-w-2xl">
                        Trag den Himmelsfond am{" "}
                        <span className="italic text-[#f0c674]">Handgelenk.</span>
                    </h2>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {products.map((p, i) => (
                    <ProductCard
                        key={p.id}
                        product={p}
                        index={i}
                        onBuy={() => setOpenProduct(p)}
                    />
                ))}
            </div>

            {openProduct && (
                <CheckoutModal
                    product={openProduct}
                    rates={rates}
                    onClose={() => setOpenProduct(null)}
                />
            )}
        </section>
    );
}

function ProductCard({ product, index, onBuy }) {
    const soldOut = !product.available || (product.stock ?? 0) <= 0;
    const fundShare = Number(product.fund_share_eur || 0);
    const price = Number(product.price_eur || 0);

    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.08 }}
            className={`glass overflow-hidden flex flex-col ${
                soldOut ? "opacity-60" : "gold-glow"
            }`}
            data-testid={`shop-card-${product.id}`}
        >
            <div className="relative aspect-[4/3] bg-[#040710]/40">
                {product.image_url ? (
                    <img
                        src={absoluteImageUrl(product.image_url)}
                        alt={product.name}
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="lazy"
                    />
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-[#94a3b8] text-sm">
                        Kein Bild
                    </div>
                )}
                {soldOut && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                        <span className="text-[#f0c674] font-display text-2xl tracking-wide">
                            Ausverkauft
                        </span>
                    </div>
                )}
                {!soldOut && product.stock <= 5 && (
                    <span
                        className="absolute top-3 right-3 text-[10px] font-mono uppercase tracking-widest bg-[#f0c674]/15 text-[#f0c674] px-2.5 py-1 rounded-full backdrop-blur"
                        data-testid={`shop-stock-${product.id}`}
                    >
                        Nur noch {product.stock} Stück
                    </span>
                )}
            </div>
            <div className="p-6 flex flex-col flex-1">
                <h3 className="font-display text-2xl mb-2">{product.name}</h3>
                {product.tagline && (
                    <p className="text-sm text-[#f0c674]/80 mb-3 italic">
                        {product.tagline}
                    </p>
                )}
                <p className="text-sm text-[#94a3b8] leading-relaxed mb-5 flex-1">
                    {product.description}
                </p>
                <div className="flex items-end justify-between gap-4 mb-5">
                    <div>
                        <div className="font-display text-3xl text-[#f8fafc]">
                            {price.toFixed(2).replace(".", ",")}{" "}
                            <span className="text-xl text-[#f0c674]">€</span>
                        </div>
                        <div className="text-xs text-[#94a3b8] mt-1">
                            zzgl. Versand
                        </div>
                    </div>
                    {fundShare > 0 && (
                        <div className="text-right">
                            <div className="text-[10px] font-mono uppercase tracking-widest text-[#f0c674]/70">
                                In den Fond
                            </div>
                            <div className="font-display text-xl text-[#f0c674]">
                                {fundShare.toFixed(2).replace(".", ",")} €
                            </div>
                        </div>
                    )}
                </div>
                <button
                    type="button"
                    onClick={onBuy}
                    disabled={soldOut}
                    className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid={`shop-buy-${product.id}`}
                >
                    {soldOut ? "Ausverkauft" : "Armband bestellen →"}
                </button>
            </div>
        </motion.div>
    );
}

function CheckoutModal({ product, rates, onClose }) {
    const [email, setEmail] = useState("");
    const [country, setCountry] = useState("DE");
    const [submitting, setSubmitting] = useState(false);

    async function handleSubmit(e) {
        e.preventDefault();
        if (!email) return toast.error("Bitte E-Mail eingeben");
        setSubmitting(true);
        try {
            const r = await shopApi.createCheckout({
                productId: product.id,
                email,
                country,
            });
            window.location.href = r.url;
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    }

    const shipping = Number(rates[country] || 0);
    const price = Number(product.price_eur || 0);
    const total = price + shipping;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
            onClick={onClose}
            data-testid="shop-modal"
        >
            <div
                className="glass max-w-md w-full p-6 sm:p-8 max-h-[90vh] overflow-y-auto relative"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-4 right-4 text-[#94a3b8] hover:text-[#f0c674]"
                    aria-label="Schließen"
                    data-testid="shop-modal-close"
                >
                    <X size={20} />
                </button>
                <div className="label mb-3">Bestellung</div>
                <h3 className="font-display text-2xl mb-2">{product.name}</h3>
                <p className="text-sm text-[#94a3b8] mb-6 leading-relaxed">
                    Wir leiten dich gleich zu Stripe weiter, wo du Bezahlung und
                    Lieferadresse eingibst. Den Versand übernehmen wir
                    persönlich.
                </p>

                <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                        <label className="block text-sm text-[#94a3b8] mb-2">
                            Lieferland <span className="text-[#f0c674]">*</span>
                        </label>
                        <div className="grid grid-cols-3 gap-2" data-testid="shop-country-select">
                            {["DE", "AT", "CH"].map((c) => (
                                <button
                                    type="button"
                                    key={c}
                                    onClick={() => setCountry(c)}
                                    className={`px-3 py-2.5 rounded-xl border text-sm transition ${
                                        country === c
                                            ? "border-[#f0c674] bg-[#f0c674]/10 text-[#f0c674]"
                                            : "border-white/10 text-[#94a3b8] hover:border-[#f0c674]/40"
                                    }`}
                                    data-testid={`shop-country-${c}`}
                                >
                                    {COUNTRY_LABEL[c]}
                                </button>
                            ))}
                        </div>
                    </div>

                    <label className="block">
                        <span className="block text-sm text-[#94a3b8] mb-2">
                            Deine E-Mail-Adresse{" "}
                            <span className="text-[#f0c674]">*</span>
                        </span>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            placeholder="z. B. du@example.com"
                            className="input-field"
                            data-testid="shop-email"
                        />
                    </label>

                    <div className="bg-[#040710]/60 border border-white/10 rounded-xl p-4 text-sm space-y-2" data-testid="shop-summary">
                        <Row label="Armband" value={`${price.toFixed(2).replace(".", ",")} €`} />
                        <Row
                            label={`Versand (${COUNTRY_LABEL[country]})`}
                            value={`${shipping.toFixed(2).replace(".", ",")} €`}
                        />
                        <div className="h-px bg-white/10 my-2" />
                        <Row
                            label="Gesamt"
                            value={`${total.toFixed(2).replace(".", ",")} €`}
                            strong
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={submitting}
                        className="btn-primary w-full"
                        data-testid="shop-submit"
                    >
                        {submitting
                            ? "Bitte warten…"
                            : `Weiter zu Stripe (${total.toFixed(2).replace(".", ",")} €) →`}
                    </button>
                </form>
            </div>
        </div>
    );
}

function Row({ label, value, strong }) {
    return (
        <div className="flex items-center justify-between gap-3">
            <span className={strong ? "text-[#f8fafc]" : "text-[#94a3b8]"}>{label}</span>
            <span className={strong ? "text-[#f0c674] font-medium" : "text-[#f8fafc]"}>
                {value}
            </span>
        </div>
    );
}
